import { useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import { MOCK_CHANGEOVER_CREWING } from '../mock/mockData';
import CPMockBadge from './CPMockBadge';

const LEVELS = ['L1', 'L2', 'L3'] as const;
const ROOM_TYPES = ['High Speed 1', 'High Speed 2', 'Medium Speed', 'Low Speed 1', 'Low Speed 2', 'Overlay'] as const;
type CrewingRow = { PA: number; MO: number; QI: number };

const CO_ROWS = LEVELS.flatMap(level =>
  ROOM_TYPES.map(rt => ({ level, rt, key: `${level}_${rt}`, label: rt }))
);

const C = { primary: '#522d6e', primaryLight: '#7c5aa4', white: '#ffffff', grey: '#e5e7eb', greyLight: '#f3f4f6', greyDark: '#6b7280', rowAlt: '#F7F3FE' };

const LEVEL_COLORS: Record<string, { bg: string; text: string }> = {
  L1: { bg: '#eff6ff', text: '#1d4ed8' },
  L2: { bg: '#faf5ff', text: '#7e22ce' },
  L3: { bg: '#fff7ed', text: '#c2410c' },
};

const CPChangeoverCrewingTab = observer(() => {
  const store = useCapacityStore();
  const [editing, setEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rows, setRows] = useState<Record<string, CrewingRow>>(
    () => Object.fromEntries(CO_ROWS.map(({ key }) => [key, { ...(MOCK_CHANGEOVER_CREWING[key] ?? { PA: 1, MO: 1, QI: 1 }) }]))
  );
  const [original, setOriginal] = useState<Record<string, CrewingRow>>(() =>
    Object.fromEntries(CO_ROWS.map(({ key }) => [key, { ...(MOCK_CHANGEOVER_CREWING[key] ?? { PA: 1, MO: 1, QI: 1 }) }]))
  );
  const [rawValues, setRawValues] = useState<Record<string, string>>({});

  const handleEdit = () => {
    const raw: Record<string, string> = {};
    CO_ROWS.forEach(({ key }) => {
      (['PA', 'MO', 'QI'] as const).forEach(role => {
        raw[`${key}_${role}`] = String(rows[key]?.[role] ?? 0);
      });
    });
    setRawValues(raw);
    setOriginal(JSON.parse(JSON.stringify(rows)));
    setEditing(true);
  };
  const handleCancel = () => { setRawValues({}); setRows(JSON.parse(JSON.stringify(original))); setEditing(false); };
  const handleChange = (cellId: string, raw: string) => {
    if (!/^(\d*\.?\d*)$/.test(raw)) return;
    setRawValues(prev => ({ ...prev, [cellId]: raw }));
  };
  const handleBlur = (cellId: string, key: string, role: keyof CrewingRow) => {
    const raw = rawValues[cellId] ?? '';
    const v = parseFloat(raw);
    const val = Number.isNaN(v) ? 0 : v;
    setRows(prev => ({ ...prev, [key]: { ...prev[key], [role]: val } }));
    setRawValues(prev => ({ ...prev, [cellId]: String(val) }));
  };

  const handleSave = useCallback(async () => {
    // Flush any pending raw values into rows before saving
    setRows(prev => {
      const next = { ...prev };
      Object.entries(rawValues).forEach(([cellId, raw]) => {
        const lastUnderscore = cellId.lastIndexOf('_');
        const key = cellId.slice(0, lastUnderscore);
        const role = cellId.slice(lastUnderscore + 1) as keyof CrewingRow;
        const v = parseFloat(raw);
        const val = Number.isNaN(v) ? 0 : v;
        next[key] = { ...next[key], [role]: val };
      });
      return next;
    });
    setRawValues({});
    setEditing(false);
    setIsSaving(true);
    try {
      await store.applyPlanningSettings(store.startYearMonth, store.planningParams);
      toast.success('Changeover crewing updated and dashboard recalculated.', { position: 'bottom-right', autoClose: 4000 });
    } finally {
      setIsSaving(false);
    }
  }, [store]);

  const actionButtons = (
    <div style={{ display: 'flex', gap: 8 }}>
      {editing ? (
        <>
          <Button label="Cancel" icon="pi pi-times" size="small" severity="secondary" onClick={handleCancel} />
          <Button label={isSaving ? 'Updating…' : 'Save & Update Dashboard'} icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-save'} size="small" style={{ background: C.primary, borderColor: C.primary }} onClick={handleSave} disabled={isSaving} />
        </>
      ) : (
        <Button label="Edit" icon="pi pi-pencil" size="small" severity="secondary" onClick={handleEdit} />
      )}
    </div>
  );

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontSize: 13 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', padding: '0.75rem 1rem', background: C.white, borderRadius: 8, border: `1px solid ${C.grey}`, gap: '1rem', flexWrap: 'wrap' as const }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <CPMockBadge />
          <i className="pi pi-refresh" style={{ color: C.primary }} />
          <span style={{ fontWeight: 700, color: C.primary, fontSize: 13 }}>Changeover Crewing</span>
          <span style={{ fontSize: 11, color: C.greyDark }}>Headcount per role per clean level × room type during changeovers</span>
        </div>
        {actionButtons}
      </div>

      {/* Table */}
      <div style={{ border: `1px solid ${C.grey}`, borderRadius: 8, background: C.white, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: C.primaryLight }}>
              <th style={{ color: C.white, fontWeight: 500, fontSize: 12, padding: '0.6rem 1rem', textAlign: 'center', width: 80 }}>Clean Level</th>
              <th style={{ color: C.white, fontWeight: 500, fontSize: 12, padding: '0.6rem 1rem', textAlign: 'left' }}>Room Type</th>
              {(['PA', 'MO', 'QI'] as const).map(r => (
                <th key={r} style={{ color: C.white, fontWeight: 500, fontSize: 12, padding: '0.6rem 1rem', textAlign: 'center', width: 120 }}>{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CO_ROWS.map(({ level, rt, key, label }, idx) => {
              const lc = LEVEL_COLORS[level];
              return (
                <tr key={key} style={{ background: idx % 2 ? C.rowAlt : C.white }}>
                  <td style={{ padding: '0.4rem 1rem', textAlign: 'center', borderBottom: `1px solid ${C.greyLight}` }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: lc.text, background: lc.bg, padding: '2px 8px', borderRadius: 5 }}>{level}</span>
                  </td>
                  <td style={{ padding: '0.4rem 1rem', fontWeight: 500, color: '#1e293b', borderBottom: `1px solid ${C.greyLight}` }}>{label}</td>
                  {(['PA', 'MO', 'QI'] as const).map(role => (
                    <td key={role} style={{ padding: '0.4rem 1rem', textAlign: 'center', borderBottom: `1px solid ${C.greyLight}` }}>
                      {editing ? (
                        <input type="text" inputMode="decimal" value={rawValues[`${key}_${role}`] ?? ''}
                          onChange={e => handleChange(`${key}_${role}`, e.target.value)}
                          onBlur={() => handleBlur(`${key}_${role}`, key, role)}
                          onFocus={() => { if (rawValues[`${key}_${role}`] === '0') setRawValues(prev => ({ ...prev, [`${key}_${role}`]: '' })); }}
                          style={{ width: 70, textAlign: 'center', fontSize: 12, padding: '3px 6px', border: `1px solid ${C.grey}`, borderRadius: 4, outline: 'none' }}
                        />
                      ) : (
                        <span style={{ fontWeight: 500, color: '#374151' }}>{rows[key]?.[role] ?? '—'}</span>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: 8 }}>
          {actionButtons}
        </div>
      )}
    </div>
  );
});

export default CPChangeoverCrewingTab;
